import React, { useMemo, useState } from 'react';
import { toast } from '../../../lib/toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteRole, getRoles, type RoleListItem } from '../api/roles';
import RoleFormDrawer from './RoleFormDrawer';
import DataTable from '../../../components/DataTable';
import { ListPage, buildCogActionsColumn } from '../../../components/list';

const RolesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data: roles = [], isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: getRoles,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRole,
    onSuccess: () => {
      toast.success('Role deleted');
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
    },
    onError: () => toast.error('Failed to delete role'),
  });

  const openCreate = () => {
    setEditingId(null);
    setDrawerOpen(true);
  };

  const openEdit = (id: string) => {
    setEditingId(id);
    setDrawerOpen(true);
  };

  const columns = useMemo(
    () => [
      { title: 'Name', dataIndex: 'name', key: 'name' },
      { title: 'Description', dataIndex: 'description', key: 'description' },
      { title: 'Permissions', dataIndex: 'permissionCount', key: 'permissionCount' },
      { title: 'Users', dataIndex: 'userCount', key: 'userCount' },
      buildCogActionsColumn<RoleListItem>({
        onEdit: (record) => openEdit(record.id),
        onDelete: (record) => deleteMutation.mutate(record.id),
        deleteConfirm: () => ({
          title: 'Delete role',
          content: 'Are you sure you want to delete this role?',
        }),
        editPermission: 'roles.edit',
        deletePermission: 'roles.delete',
        deleteDisabled: (record) => record.userCount > 0,
        rowNumber: (_record, index) => index + 1,
      }),
    ],
    [deleteMutation],
  );

  return (
    <>
      <ListPage
        title="Roles"
        subtitle="Define permission bundles and assign them to users."
        primaryAction={{
          permission: 'roles.create',
          label: 'Add role',
          onClick: openCreate,
        }}
      >
        <DataTable
          rowKey="id"
          loading={isLoading}
          dataSource={roles}
          columns={columns}
          bordered
          striped
          emptyText="No roles yet."
        />
      </ListPage>
      <RoleFormDrawer
        open={drawerOpen}
        roleId={editingId}
        onClose={() => setDrawerOpen(false)}
        onSaved={() => {
          setDrawerOpen(false);
          void queryClient.invalidateQueries({ queryKey: ['roles'] });
        }}
      />
    </>
  );
};

export default RolesPage;
